import { IdTokenResult } from '@firebase/auth';
import { createAction, props } from '@ngrx/store';
import { FirebaseUser } from '@vmspdb/ui/domain';

/**
 * Everything on Firebase's `IdTokenResult` except the encoded JWT. The store
 * (and so `localStorage` and the session recording) must never hold the
 * encoded token; selectors only read the claims.
 */
export type AuthTokenClaims = Omit<IdTokenResult, 'token'>;

/** Drops the encoded token, keeping the claims and token metadata. */
export function toAuthTokenClaims(result: IdTokenResult): AuthTokenClaims {
  const {
    claims,
    expirationTime,
    issuedAtTime,
    authTime,
    signInProvider,
    signInSecondFactor,
  } = result;
  return {
    claims,
    expirationTime,
    issuedAtTime,
    authTime,
    signInProvider,
    signInSecondFactor,
  };
}

export const setAuthProjectID = createAction(
  '[Auth] Set Project ID',
  props<{ projectID: string }>(),
);

export const loadAuths = createAction('[Auth] Load Auths');

export const setAuthUser = createAction(
  '[Auth] Set User',
  props<{ user: FirebaseUser }>(),
);

export const setAuthToken = createAction(
  '[Auth] Set User Token',
  props<{ token: AuthTokenClaims | null }>(),
);

// The return url travels with `resetAuth` itself (see `resetAuth$` in
// auth.effects.ts) rather than through a separate NgRx round-trip - a
// dispatch-then-select pair raced AuthGuard's own navigation and lost
// (docs/e2e-backlog.md#bug-025).
export const resetAuth = createAction(
  '[Auth] Reset',
  props<{ returnUrl?: string }>(),
);

export const noopAuth = createAction('[Auth] Noop Auth');
